import { Account } from '../interfaces/accounting.interface';

/** Accounts nobody hangs under: the ones a document may post to (group/header accounts are excluded). */
export function leafAccounts(accounts: Account[]): Account[] {
    const parentIds = new Set(accounts.map(a => a.parentAccountId).filter((p): p is string => !!p));
    return accounts.filter(a => !a.id || !parentIds.has(a.id));
}

/**
 * Where a customer's money can be deposited: cash/bank posting accounts.
 * If the chart has not tagged any account as cash, fall back to every posting asset account
 * rather than showing an empty picker.
 */
export function depositAccountOptions(assetAccounts: Account[]): Account[] {
    // Bills, invoices and their payments / receipts are in BDT: never offer a foreign-currency bank account
    // (the server refuses it too).
    const leaves = leafAccounts(assetAccounts).filter(a => !a.foreignCurrencyCode);
    const cash = leaves.filter(a => a.postingClass === 'CASH');
    return cash.length > 0 ? cash : leaves;
}

/**
 * Polish 4: where a customer remittance is deposited -- a bank account's GL account only (petty cash, FDRs and the
 * like are cash-class too but are not where a customer's money lands). With no bank account registered, or when the
 * bank accounts could not be read, the plain options are returned so the picker is never emptied by a missing setup.
 */
export function bankDepositOptions(options: Account[], bankGlAccountIds: string[] | null, method?: string): Account[] {
    if (method === 'CASH') return options;   // a cash receipt is not banked through a bank account
    if (!bankGlAccountIds || bankGlAccountIds.length === 0) return options;
    const ids = new Set(bankGlAccountIds);
    const banks = options.filter(a => !!a.id && ids.has(a.id));
    return banks.length > 0 ? banks : options;   // none of the bank GL accounts qualify: do not empty the picker
}

const CURRENCY_TOKEN = /(?:^|[^A-Za-z])(USD|EUR|GBP|INR|CNY|JPY|AED|SAR|SGD|AUD|CAD|CHF)(?:[^A-Za-z]|$)/;

/**
 * Soft hint for a plain cash account whose name marks it as foreign currency (e.g. "Cash in hand - USD"). Only the
 * foreign-currency BANK accounts are reliably marked (and refused); a name is a guess, so this warns, never blocks.
 */
export function foreignCurrencyNameHint(account?: Pick<Account, 'name' | 'foreignCurrencyCode'> | null): string | null {
    if (!account || account.foreignCurrencyCode) return null;
    const m = CURRENCY_TOKEN.exec(account.name ?? '');
    return m ? `This account looks like a ${m[1]} account, but this document is in BDT.` : null;
}

/**
 * The receipt method that fits a deposit account: a bank account defaults to bank transfer, a mobile-wallet account
 * to mobile banking and anything else (cash in hand, petty cash) to cash. Only a default: the user can still change it.
 */
export function defaultReceiptMethodForAccount(account?: Pick<Account, 'name'> | null): 'CASH' | 'BANK_TRANSFER' | 'MOBILE_BANKING' {
    const name = account?.name ?? '';
    if (/bkash|nagad|rocket|mobile|wallet|mfs/i.test(name)) return 'MOBILE_BANKING';
    if (/bank/i.test(name)) return 'BANK_TRANSFER';
    return 'CASH';
}

export interface StoredAccountCheck {
    label: string;
    id?: string | null;
    /** The valid choices (leaf accounts of the right type). */
    options: Account[];
    /** Every account of that type, to say WHY the stored one is not valid. */
    all: Account[];
}

/**
 * Stored account ids that are no longer a valid choice (now a group account, inactive/deleted, or of another type), with
 * a message naming the account. Empty ids are not problems.
 */
export function storedAccountProblems(checks: StoredAccountCheck[]): { label: string; message: string }[] {
    const out: { label: string; message: string }[] = [];
    for (const c of checks) {
        if (!c.id || c.options.some(a => a.id === c.id)) continue;
        const found = c.all.find(a => a.id === c.id);
        const name = found ? `${found.code} - ${found.name}` : 'the stored account';
        const why = !found ? 'is no longer available (inactive, deleted or of another type)'
            : 'is now a group account with sub-accounts';
        out.push({ label: c.label, message: `${c.label}: ${name} ${why}.` });
    }
    return out;
}

// ---- B4-C: foreign-currency documents ----
/**
 * Deposit/payment accounts for a document in `currency`: BDT documents see the BDT accounts (as depositAccountOptions);
 * a foreign-currency document sees the BDT accounts plus the accounts held in that same currency.
 */
export function depositAccountOptionsFor(assetAccounts: Account[], currency?: string | null): Account[] {
    if (!currency || currency === 'BDT') return depositAccountOptions(assetAccounts);
    const leaves = leafAccounts(assetAccounts).filter(a => !a.foreignCurrencyCode || a.foreignCurrencyCode === currency);
    const cash = leaves.filter(a => a.postingClass === 'CASH');
    return cash.length > 0 ? cash : leaves;
}
// ---- end B4-C ----

// ---- FW-2B: the cash of a cash sale lands in a cash/bank-class account only (the server enforces the same rule) ----
/** BDT leaf asset accounts classed CASH. No fallback to other assets: an AR or inventory account is never a deposit account. */
export function cashSaleDepositOptions(assetAccounts: Account[]): Account[] {
    return leafAccounts(assetAccounts).filter(a => !a.foreignCurrencyCode && a.postingClass === 'CASH');
}
// ---- end FW-2B ----
